import { getPlatformId, getProfileType } from '@/services/customerDataSuite';
import {
  PageType,
  ProfileLanguageMapping,
  ProfilePlaybackLanguageMapping,
  ScreenName,
} from '@/services/customerDataSuite/constants';
import { getSystem } from '@/system';

type SectionLevel = {
  section_level_1: string;
  section_level_2: string;
  section_level_3: string;
};

let sectionLevels: SectionLevel = {
  section_level_1: '',
  section_level_2: '',
  section_level_3: '',
};

const popFromLevels = (): void => {
  if (sectionLevels.section_level_3) {
    sectionLevels.section_level_3 = '';
  } else if (sectionLevels.section_level_2) {
    sectionLevels.section_level_2 = '';
  } else {
    sectionLevels.section_level_1 = '';
  }
};

let prevScreenTitle: string = '';
let prevUsedTitle: string = '';
let prevPageUrl: string = '';

const level1_screens = [
  ScreenName.LANDING,
  ScreenName.SETTINGS,
  ScreenName.SEARCH,
  ScreenName.PROFILE,
  ScreenName.SWITCH_PROFILE,
  ScreenName.EDIT_PROFILE,
  ScreenName.SIGN_IN_CRAVE,
  ScreenName.SIGN_IN_PROVIDER,
  ScreenName.RESET_PASSWORD,
  ScreenName.RESET_PASSWORD_CONFIRMATION,
  ScreenName.FRONTDOOR_SIGN_IN_SELECTION,
  ScreenName.FRONTDOOR,
  ScreenName.SUBSCRIBE,
  ScreenName.SPLASH,
  ScreenName.PRODUCT_INTRO,
  ScreenName.ONBOARDING_LANGUAGE,
];

const level2_screens = [
  ScreenName.SHOW_PAGE,
  ScreenName.AUDIO,
  ScreenName.AUTOPLAY,
  ScreenName.MANAGE,
  ScreenName.FAQS,
  ScreenName.CONTACT,
  ScreenName.PRIVACY,
  ScreenName.TERMS,
  ScreenName.RATING,
  ScreenName.DISPLAY_LANGUAGE,
  ScreenName.SIGN_OUT,
  ScreenName.ADD_PROFILE,
  ScreenName.RESET_REMOVE_PIN,
  ScreenName.GRID,
  ScreenName.LIVE_EVENT,
];

const level3_screens = [
  ScreenName.LANGUAGES,
  ScreenName.PLAYER,
  ScreenName.MORE_INFO,
  ScreenName.EDIT_NAME,
  ScreenName.CONTENT_ACCESS,
  ScreenName.ENTER_PIN,
  ScreenName.PROFILE_DISPLAY_LANGUAGE,
  ScreenName.PROFILE_AUDIO_SUBTITLES,
  ScreenName.MANAGE_SUBSCRIPTION,
  ScreenName.WAYSTOWATCH,
];

const getLevelDepth = (screen: string): number => {
  if (level1_screens.includes(screen as ScreenName)) return 1;
  if (level2_screens.includes(screen as ScreenName)) return 2;
  if (level3_screens.includes(screen as ScreenName)) return 3;
  return 0;
};

const getTitleForSection = (
  screen: ScreenName,
  displayTitle: string,
  screenTitle: string
): string => {
  const useDisplayTitleScreens = new Set<ScreenName>([
    ScreenName.LANDING,
    ScreenName.SHOW_PAGE,
    ScreenName.MORE_INFO,
    ScreenName.PRODUCT_INTRO,
    ScreenName.WAYSTOWATCH,
    ScreenName.GRID,
    ScreenName.LIVE_EVENT,
    // Add more where displayTitle is preferred
  ]);

  if (useDisplayTitleScreens.has(screen)) {
    return displayTitle;
  }

  return screenTitle;
};

const getPageType = (screen: ScreenName) => {
  if ([ScreenName.LANDING, ScreenName.SEARCH, ScreenName.SETTINGS].includes(screen)) {
    return PageType.LandingPage;
  } else if ([ScreenName.SHOW_PAGE].includes(screen)) {
    return PageType.MediaPage;
  } else if ([ScreenName.LIVE_EVENT].includes(screen)) {
    return PageType.LivePage;
  } else {
    return PageType.LandingPage;
  }
};

export const getScreenViewAttributes = (
  selectedProfile: any,
  screenTitle: string,
  displayTitle: string,
  pathname: string,
  searchParams: URLSearchParams,
  smartId: string
) => {
  /*
  Returns all attributes required to log a event to mParticle.
  */
  const profile_id = selectedProfile?.id ?? '';
  const profile_language =
    ProfileLanguageMapping[
      selectedProfile?.uiLanguage as keyof typeof ProfileLanguageMapping
    ] || null;

  const profile_playback_language = selectedProfile
    ? (selectedProfile?.playbackLanguages || [])
        .map((l: string) => {
          return ProfilePlaybackLanguageMapping?.[
            l as keyof typeof ProfilePlaybackLanguageMapping
          ];
        })
        .join(', ')
    : null;

  const profile_type = getProfileType(selectedProfile?.maturity);

  const currentDepth = getLevelDepth(screenTitle);
  const prevDepth = getLevelDepth(prevScreenTitle);

  const title = getTitleForSection(
    screenTitle as ScreenName,
    displayTitle ?? '',
    screenTitle
  );

  const currentUrl = window.location.toString();
  const referringScreenTitle = prevUsedTitle;
  const referringPageUrl = prevPageUrl;

  if (currentDepth > prevDepth) {
    // Going deeper — push
    if (currentDepth === 1) {
      sectionLevels.section_level_1 = title;
      sectionLevels.section_level_2 = '';
      sectionLevels.section_level_3 = '';
    } else if (currentDepth === 2) {
      sectionLevels.section_level_2 = title;
      sectionLevels.section_level_3 = '';
    } else if (currentDepth === 3) {
      sectionLevels.section_level_3 = title;
    }
  } else if (currentDepth < prevDepth) {
    // Going back — pop
    popFromLevels();
  } else if (currentDepth === prevDepth) {
    // Same level, different screen
    if (currentDepth === 1) sectionLevels.section_level_1 = title;
    if (currentDepth === 2) sectionLevels.section_level_2 = title;
    if (currentDepth === 3) sectionLevels.section_level_3 = title;
  }
  prevScreenTitle = screenTitle;
  prevUsedTitle = title;
  prevPageUrl = currentUrl;

  const system = getSystem();
  const platformId = getPlatformId(system.id);
  const type = getPageType(screenTitle as ScreenName);

  return {
    platform_id: platformId,
    title: title,
    url: window.location.toString(),
    search: searchParams.toString(),
    path: pathname,
    section_level_1: sectionLevels.section_level_1,
    section_level_2: sectionLevels.section_level_2,
    section_level_3: sectionLevels.section_level_3,
    profile_language: profile_language,
    profile_playback_language: profile_playback_language,
    profile_id: profile_id,
    profile_type: profile_type,
    referring_screen_title: referringScreenTitle,
    type: type,
    referral_id: '',
    referring_page_url: referringPageUrl,
    smart_id: smartId,
    keywords: '',
    tags: '',
  };
};
